import { Menu } from "@base-ui/react/menu"
import React, { useId } from "react"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { stopPropagation } from "shared/lib/stopPropagation"
import { useEvent } from "shared/lib/useEvent"
import { Action } from "shared/types/Action"
import { IconSize } from "shared/types/Icon"
import { Icon } from "shared/ui/icon/Icon"
import { IconName } from "shared/ui/icon/__generated__/IconName"
import "shared/ui/menu/styles/_menu.scss"
import { Tooltip } from "shared/ui/tooltip/Tooltip"

import { RowMenuTooltipContent } from "./RowMenuTooltipContent"

export interface ProductPositionRowMenuCellHandle {
  openMenu: () => void
}

interface ProductPositionRowMenuCellProps {
  actions: Action[]
  isFocused?: boolean
  cellProps?: Omit<React.HTMLProps<HTMLDivElement>, "ref">
}

export const ProductPositionRowMenuCell = React.memo(
  React.forwardRef<
    ProductPositionRowMenuCellHandle,
    ProductPositionRowMenuCellProps
  >(({ actions, isFocused = false, cellProps }) => {
    const cellId = useId()
    const { t } = useTranslation()

    const handleClick: React.MouseEventHandler<HTMLDivElement> = useEvent(e => {
      e.stopPropagation()
      cellProps?.onClick?.(e)
    })
    const handleKeyDown: React.KeyboardEventHandler = useEvent(e => {
      if (e.key === "ArrowDown") {
        e.stopPropagation()
      }
    })

    if (actions.length === 0)
      return <div className="table-row-cell table-row-cell--menu" />

    return (
      <Menu.Root>
        <Tooltip
          placement="top"
          content={isFocused ? <RowMenuTooltipContent isFocused /> : null}
        >
          <Menu.Trigger
            id={cellId}
            nativeButton={false}
            render={<div />}
            {...(cellProps as Menu.Trigger.Props)}
            onClick={
              handleClick as unknown as React.MouseEventHandler<HTMLButtonElement>
            }
            onKeyDown={handleKeyDown}
            className="table-row-cell table-row-cell--menu"
            aria-label={t("Menu.Menu")}
          >
            <span data-row-kebab="" aria-hidden="true">
              <Icon name={IconName.KEBAB} size={IconSize.SMALL} />
            </span>
          </Menu.Trigger>
        </Tooltip>
        <Menu.Portal>
          <Menu.Positioner
            className="menu__positioner"
            side="bottom"
            align="start"
            sideOffset={4}
            positionMethod="fixed"
            collisionAvoidance={{ align: "flip", side: "flip" }}
          >
            <Menu.Popup
              className="menu__popup"
              aria-labelledby={cellId}
              onClick={stopPropagation}
              onKeyDown={stopPropagation}
            >
              {actions.map(action => (
                <React.Fragment key={action.id ?? action.label}>
                  <Menu.Item
                    className={classNames("btn", "btn-menu", "menu__item", {
                      "btn--danger": action.isDanger
                    })}
                    disabled={action.disabled}
                    onClick={action.onClick}
                  >
                    {action.icon ? (
                      <Icon name={action.icon} size={IconSize.SMALL} />
                    ) : null}
                    <span>{action.label}</span>
                  </Menu.Item>
                </React.Fragment>
              ))}
            </Menu.Popup>
          </Menu.Positioner>
        </Menu.Portal>
      </Menu.Root>
    )
  })
)

ProductPositionRowMenuCell.displayName = "ProductPositionRowMenuCell"
